import { useState, useEffect, useRef } from "react";
import { useOrganization } from "../../contexts/OrganizationContext";
import { Zap, Play, FileText, BookOpen, ArrowLeft, MoreVertical, GitBranch, Rocket, Pin, XCircle } from "lucide-react";
import MobileMenuButton from "../Sidebar/MobileMenuButton";
import { useHeaderSlot } from "../Sidebar/MobileNavContext";
import { Badge } from "../ui/badge";
import ConfirmDialog from "../ui/ConfirmDialog";
import WorkflowsList from "./WorkflowsList";
import WorkflowCanvas from "./WorkflowCanvas";
import TriggersPanel from "./TriggersPanel";
import SettingsPanel from "./SettingsPanel";
import RunInspector from "./RunInspector";
import RunsView from "./RunsView";
import WorkflowVersions from "./WorkflowVersions";
import DeploymentStrategy from "./DeploymentStrategy";
import TestPinsPanel from "./TestPinsPanel";

const TopNav = ({ activeView, onViewChange, onBackOverride, workflowName, workflowStatus, versionInfo, triggerCount, embedded, projectSelector, onSave, onTest, onRun, onPublish, onWorkflowNameChange, workflowId, onShowVersions, onShowDeployment, onShowTestPins, onDiscardDraft, isDraftEditMode, onEnableDraftEdit }) => {
  const isEditorView = ["editor", "triggers", "workflow_runs"].includes(activeView);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Esta barra es la cabecera de la pantalla —en la lista y también en el
  // editor—, así que le toca a ella pintar la hamburguesa. Antes el editor no
  // reclamaba el hueco y el shell dibujaba la suya flotando en la esquina, que
  // es exactamente donde está el nombre del workflow: se leía "ew Workflow".
  const isPrimaryHeader = useHeaderSlot();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  if (isEditorView) {
    return (
      // Dos filas en móvil. En una sola, el nombre con sus insignias más las
      // tres pestañas y los tres botones de acción pasan de 850px: lo que se
      // veía era el nombre y nada más, con todo lo demás fuera de pantalla.
      <div className="flex flex-col gap-2 border-b border-white/10 px-3 py-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0 lg:px-6 lg:py-3">
        <div className="flex min-w-0 items-center gap-2 lg:gap-4">
          {isPrimaryHeader && <MobileMenuButton className="h-9 w-9 rounded-lg" />}
          <button
            onClick={() => onBackOverride ? onBackOverride() : onViewChange("workflows")}
            aria-label="Back to workflows"
            className="shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:bg-white/5 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <input
            type="text"
            value={workflowName || ''}
            onChange={(event) => onWorkflowNameChange?.(event.target.value)}
            className="min-w-0 flex-1 border-b border-transparent bg-transparent text-sm font-semibold text-white focus:border-white/30 focus:outline-none lg:min-w-[220px] lg:max-w-[360px] lg:flex-none"
            placeholder="Workflow name"
          />
          
          {workflowStatus && (
            <div className="flex shrink-0 items-center gap-2">
              <Badge variant="outline" className={`text-[10px] border ${
                workflowStatus === "active" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                workflowStatus === "draft" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                "bg-gray-500/20 text-gray-400 border-gray-500/30"
              }`}>
                {workflowStatus}
              </Badge>
              {versionInfo && (
                <Badge variant="outline" className="text-[10px] border bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                  v{versionInfo}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Pestañas y acciones comparten la segunda fila y se desplazan: en
            390px caben unas cinco de las siete cosas que hay aquí. */}
        <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] lg:gap-3 [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => onViewChange("editor")}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
              activeView === "editor"
                ? "bg-white/5 text-white border border-white/10"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            Editor
          </button>
          <button
            onClick={() => onViewChange("triggers")}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
              activeView === "triggers"
                ? "bg-white/5 text-white border border-white/10"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            Triggers {triggerCount > 0 && <span className="ml-1 text-[10px] text-gray-500">{triggerCount}</span>}
          </button>
          <button
            onClick={() => onViewChange("workflow_runs")}
            className={`shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
              activeView === "workflow_runs"
                ? "bg-white/5 text-white border border-white/10"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            Runs
          </button>

          <div className="mx-1 h-4 w-px shrink-0 bg-white/10" />

          <button
            onClick={onTest}
            className="shrink-0 whitespace-nowrap rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-gray-300 transition-all hover:bg-white/5 hover:text-white">
            <Play className="w-3 h-3 inline mr-1" />
            Test
          </button>
          {workflowStatus === 'active' && !isDraftEditMode ? (
            <button
              onClick={onEnableDraftEdit}
              className="shrink-0 whitespace-nowrap rounded-lg border border-amber-500/30 px-3 py-1.5 text-xs font-medium text-amber-400 transition-all hover:bg-amber-500/10 hover:text-amber-300">
              Create Draft
            </button>
          ) : (
            <>
              <button
                onClick={onSave}
                className="shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium text-gray-400 transition-all hover:bg-white/5 hover:text-white">
                {isDraftEditMode ? 'Save draft' : workflowId ? 'Update' : 'Save draft'}
              </button>
              <button
                onClick={onPublish}
                className="shrink-0 whitespace-nowrap rounded-lg bg-primary px-4 py-1.5 text-xs font-medium text-white transition-all hover:bg-violet-700">
                Publish {versionInfo ? `v${parseInt(versionInfo) + 1}` : 'v1'}
              </button>
            </>
          )}

          {workflowId && (
            <div className="relative shrink-0" ref={menuRef}>
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onShowVersions?.();
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <GitBranch className="w-4 h-4" />
                    Version History
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onShowDeployment?.();
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <Rocket className="w-4 h-4" />
                    Deployment Strategy
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onShowTestPins?.();
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <Pin className="w-4 h-4" />
                    Test Pins
                  </button>
                  <div className="h-px bg-white/10 my-1" />
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onDiscardDraft?.();
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Discard Draft
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Si está embebido, mostrar solo tabs sin logo
  if (embedded) {
    return (
      // Se parte en dos líneas en móvil. Las dos pestañas, la palabra "Project"
      // y un selector de 224px piden ~430px, así que el selector —que es el
      // último— era lo que se quedaba cortado por el borde derecho.
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-4 py-3 md:flex-nowrap md:gap-1 md:px-6">
        {isPrimaryHeader && <MobileMenuButton className="h-10 w-10 rounded-xl" />}
        <nav className="flex gap-1">
          <button
            onClick={() => onViewChange("workflows")}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
              activeView === "workflows"
                ? "bg-white/5 text-white border border-white/10"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <Zap className="w-4 h-4" />
            Workflows
          </button>
          <button
            onClick={() => onViewChange("runs")}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
              activeView === "runs"
                ? "bg-white/5 text-white border border-white/10"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <Play className="w-4 h-4" />
            Runs
          </button>
        </nav>
        {projectSelector && (
          <>
            <div className="hidden flex-1 md:block" />
            <div className="flex w-full min-w-0 items-center gap-2 md:w-auto">
              {/* La etiqueta se cae en móvil: el propio selector lleva el
                  nombre del proyecto, así que "Project" delante es la palabra
                  repitiendo lo que ya se lee debajo. */}
              <span className="hidden text-xs text-gray-400 md:inline">Project</span>
              <div className="min-w-0 flex-1 md:flex-none">{projectSelector}</div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 px-6 py-3 border-b border-white/10">
      <div className="flex items-center gap-2 mr-8">
        <div className="w-8 h-8 rounded bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center text-white font-bold text-sm">
          Z
        </div>
        <span className="text-sm font-semibold text-white">Zenit Workflows</span>
        <Badge variant="outline" className="text-[9px] border bg-white/5 text-gray-400 border-white/10">
          UI concept - not implemented
        </Badge>
      </div>

      <nav className="flex gap-1">
        <button
          onClick={() => onViewChange("workflows")}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
            activeView === "workflows"
              ? "bg-white/5 text-white border border-white/10"
              : "text-gray-400 hover:text-white hover:bg-white/5"
          }`}
        >
          <Zap className="w-4 h-4" />
          Workflows
        </button>
        <button
          onClick={() => onViewChange("runs")}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-all ${
            activeView === "runs"
              ? "bg-white/5 text-white border border-white/10"
              : "text-gray-400 hover:text-white hover:bg-white/5"
          }`}
        >
          <Play className="w-4 h-4" />
          Runs
        </button>
      </nav>

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-300">
          <BookOpen className="w-4 h-4" />
          Docs
        </button>
        <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-300">
          <FileText className="w-4 h-4" />
          DM
        </button>
      </div>
    </div>
  );
};

export default function WorkflowsView({ projectId, embedded = true, projectSelector = null, onEditingChange, initialWorkflowId = null, initialCreating = false, onBackFromEditor = null }) {
  const { currentAccount } = useOrganization();
  const accountId = currentAccount?.id;
  // `initialCreating` mounts straight into the editor with a blank canvas
  // (new, unsaved workflow). `initialWorkflowId` mounts into the editor for an
  // existing workflow. Otherwise we land on the workflows list.
  // Abre en la lista de workflows. Antes abria en el board de actividad, que ya
  // no vive aqui: le dabas clic a "Workflows" y no veias workflows.
  const [activeView, setActiveView] = useState(initialWorkflowId || initialCreating ? "editor" : "workflows");
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(initialWorkflowId);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [workflowName, setWorkflowName] = useState(
    initialWorkflowId ? "Loading..." : initialCreating ? "New Workflow" : null
  );
  const [workflowStatus, setWorkflowStatus] = useState(initialCreating ? "draft" : null);
  const [workflowVersion, setWorkflowVersion] = useState(null);
  const [triggerCount, setTriggerCount] = useState(0);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  
  // Estados para modales
  const [showVersions, setShowVersions] = useState(false);
  const [showDeployment, setShowDeployment] = useState(false);
  const [showTestPins, setShowTestPins] = useState(false);
  
  const [isDraftEditMode, setIsDraftEditMode] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Refs para handlers de WorkflowCanvas
  const onSaveRef = useRef(null);
  const onTestRef = useRef(null);
  const onRunRef = useRef(null);
  const onPublishRef = useRef(null);

  // Handlers para acciones del workflow
  const handleSave = () => {
    if (onSaveRef.current) {
      onSaveRef.current();
    }
  };

  const handleTest = () => {
    if (onTestRef.current) {
      onTestRef.current();
    }
  };

  const handleRun = () => {
    if (onRunRef.current) {
      onRunRef.current();
    }
  };

  const handlePublish = () => {
    if (onPublishRef.current) {
      onPublishRef.current();
    }
  };

  const handleWorkflowNameChange = (nextName) => {
    setWorkflowName(nextName);
  };

  const handleEnableDraftEdit = () => {
    setIsDraftEditMode(true);
  };

  const handleDiscardDraft = () => {
    if (!selectedWorkflowId) return;
    setConfirmDiscard(true);
  };

  const executeDiscardDraft = async () => {
    setConfirmDiscard(false);
    try {
      const WorkflowService = (await import('../../services/workflow.service')).default;
      const result = await WorkflowService.discardDraft(selectedWorkflowId);

      if (result.success) {
        window.location.reload();
      } else {
        alert('Failed to discard draft: ' + result.error);
      }
    } catch (err) {
      alert('Failed to discard draft: ' + err.message);
    }
  };

  // Notificar al padre cuando se está editando un workflow
  useEffect(() => {
    if (onEditingChange) {
      const isEditingWorkflow = ["editor", "triggers", "workflow_runs"].includes(activeView);
      onEditingChange(isEditingWorkflow);
    }
  }, [activeView, onEditingChange]);

  const handleWorkflowSelect = (workflowId, initialData = {}) => {
    setSelectedWorkflowId(workflowId);
    setIsDraftEditMode(false);
    if (workflowId) {
      setWorkflowName("Loading...");
      // Seed version/status from the list so the TopNav is correct before the canvas loads
      setWorkflowStatus(initialData.status || null);
      setWorkflowVersion(initialData.version ?? null);
      setTriggerCount(0);
    } else {
      // Nuevo workflow
      setWorkflowName("New Workflow");
      setWorkflowStatus("draft");
      setWorkflowVersion(null);
      setTriggerCount(0);
    }
    setActiveView("editor");
  };

  const handleWorkflowDataLoaded = (workflowData) => {
    if (workflowData) {
      setWorkflowName(workflowData.name || "Untitled Workflow");
      setWorkflowStatus(workflowData.status || "draft");
      // Only update version when the canvas provides a real number.
      // The single GET /workflows/{id} response does not include version_number,
      // so we preserve whatever was seeded from the list to avoid resetting to null.
      if (workflowData.version != null) {
        setWorkflowVersion(workflowData.version);
      }
      setTriggerCount(workflowData.triggerCount || 0);
      setWorkflowSteps(workflowData.steps || []);
    }
  };

  const renderContent = () => {
    if (activeView === "workflows") {
      return <WorkflowsList projectId={projectId} onSelectWorkflow={handleWorkflowSelect} />;
    }

    if (activeView === "runs") {
      // La tabla se queda montada detrás del inspector. Desmontarla obligaba a
      // repetir la lista de ejecuciones —que tarda segundos— cada vez que se
      // cerraba una corrida.
      return (
        <div className="h-full">
          <div className={selectedRunId ? "hidden" : "h-full overflow-auto p-6"}>
            <RunsView projectId={projectId} onSelectRun={(id) => setSelectedRunId(id)} />
          </div>
          {selectedRunId && (
            <div className="h-full">
              <RunInspector
                runId={selectedRunId}
                projectId={projectId}
                onClose={() => setSelectedRunId(null)}
              />
            </div>
          )}
        </div>
      );
    }

    if (activeView === "editor" || activeView === "triggers" || activeView === "workflow_runs") {
      return (
        <div className="relative h-full">
          <div className={activeView === "editor" ? "h-full" : "h-full invisible"}>
            <WorkflowCanvas
              workflowId={selectedWorkflowId}
              projectId={projectId}
              workflowName={workflowName}
              onSave={onSaveRef}
              onTest={onTestRef}
              onRun={onRunRef}
              onPublish={onPublishRef}
              onWorkflowDataLoaded={handleWorkflowDataLoaded}
              isDraftEditMode={isDraftEditMode}
            />
          </div>
          {activeView === "triggers" && (
            <div className="absolute inset-0 bg-[#0a0a0b] z-10">
              <TriggersPanel workflowId={selectedWorkflowId} />
            </div>
          )}
          {activeView === "workflow_runs" && (
            <div className="absolute inset-0 bg-[#0a0a0b] z-10 overflow-auto">
              <div className={selectedRunId ? "hidden" : "p-6"}>
                <RunsView
                  projectId={projectId}
                  workflowId={selectedWorkflowId}
                  onSelectRun={(id) => setSelectedRunId(id)}
                />
              </div>
              {selectedRunId && (
                <RunInspector
                  runId={selectedRunId}
                  projectId={projectId}
                  onClose={() => setSelectedRunId(null)}
                />
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const isRunInspectorOpen = activeView === "runs" && !!selectedRunId;

  return (
    <div className="h-full flex flex-col text-white pt-4">
      {!isRunInspectorOpen && <TopNav
        activeView={activeView}
        onViewChange={(view) => { setSelectedRunId(null); setActiveView(view); }}
        onBackOverride={onBackFromEditor || null}
        workflowName={workflowName}
        workflowStatus={workflowStatus}
        versionInfo={workflowVersion}
        triggerCount={triggerCount}
        embedded={embedded}
        projectSelector={projectSelector}
        onSave={handleSave}
        onTest={handleTest}
        onRun={handleRun}
        onPublish={handlePublish}
        onWorkflowNameChange={handleWorkflowNameChange}
        workflowId={selectedWorkflowId}
        onShowVersions={() => setShowVersions(true)}
        onShowDeployment={() => setShowDeployment(true)}
        onShowTestPins={() => setShowTestPins(true)}
        onDiscardDraft={handleDiscardDraft}
        isDraftEditMode={isDraftEditMode}
        onEnableDraftEdit={handleEnableDraftEdit}
      />}

      <div className="flex-1 min-h-0">
        {/* Editor views y RunInspector ocupan toda la altura sin padding */}
        {["editor", "triggers", "workflow_runs", "runs"].includes(activeView) ? (
          renderContent()
        ) : (
          <div className="h-full overflow-auto p-4 md:p-6">
            {renderContent()}
          </div>
        )}
      </div>

      {/* Modals */}
      {showVersions && selectedWorkflowId && (
        <WorkflowVersions
          workflowId={selectedWorkflowId}
          projectId={projectId}
          currentVersion={workflowVersion}
          onClose={() => setShowVersions(false)}
          onVersionChange={(workflow) => {
            setWorkflowVersion(workflow?.version || null);
            window.location.reload();
          }}
        />
      )}

      {showDeployment && selectedWorkflowId && (
        <DeploymentStrategy
          workflowId={selectedWorkflowId}
          projectId={projectId}
          accountId={accountId}
          onClose={() => setShowDeployment(false)}
          onStrategyChange={() => setShowDeployment(false)}
        />
      )}

      {showTestPins && selectedWorkflowId && (
        <TestPinsPanel
          workflowId={selectedWorkflowId}
          steps={workflowSteps}
          onClose={() => setShowTestPins(false)}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDiscard}
        title="Discard Draft Changes"
        message="Discard all unpublished changes? This will restore from the last published version."
        confirmText="Discard"
        cancelText="Cancel"
        variant="danger"
        onConfirm={executeDiscardDraft}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
}
